import React, { useEffect, useState } from 'react';
import { Box, keyframes } from '@mui/material';

interface AnimatedMascotProps {
  isPlaying: boolean;
  size?: number;
}

// Keyframe animations
const breatheAnimation = keyframes`
  0%, 100% { transform: scale(1) translateY(0); }
  50% { transform: scale(1.03) translateY(-4px); }
`;

const bounceAnimation = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`;

const talkAnimation = keyframes`
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.8); }
`;

const wiggleAnimation = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 10px rgba(32, 178, 170, 0.4)); }
  50% { filter: drop-shadow(0 0 25px rgba(32, 178, 170, 0.8)); }
`;

const AnimatedMascot: React.FC<AnimatedMascotProps> = ({ isPlaying, size = 200 }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        animation: isPlaying 
          ? `${bounceAnimation} 0.5s ease-in-out infinite`
          : isHovered
          ? `${wiggleAnimation} 0.5s ease-in-out`
          : `${breatheAnimation} 3s ease-in-out infinite`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          filter: isPlaying 
            ? 'drop-shadow(0 0 20px rgba(32, 178, 170, 0.6))' 
            : 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
          animation: isPlaying ? `${pulseGlow} 1s ease-in-out infinite` : 'none',
        }}
      >
        {/* Dog/Cat Body */}
        <ellipse
          cx="100"
          cy="120"
          rx="60"
          ry="50"
          fill="#20B2AA"
          opacity="0.9"
        />
        
        {/* Head */}
        <circle
          cx="100"
          cy="70"
          r="45"
          fill="#20B2AA"
        />
        
        {/* Left Ear */}
        <ellipse
          cx="70"
          cy="45"
          rx="15"
          ry="25"
          fill="#158B87"
          transform="rotate(-20 70 45)"
        />
        
        {/* Right Ear */}
        <ellipse
          cx="130"
          cy="45"
          rx="15"
          ry="25"
          fill="#158B87"
          transform="rotate(20 130 45)"
        />
        
        {/* Left Eye */}
        <circle
          cx="85"
          cy="65"
          r="8"
          fill="#2C3E50"
        />
        
        {/* Right Eye */}
        <circle
          cx="115"
          cy="65"
          r="8"
          fill="#2C3E50"
        />
        
        {/* Eye Sparkles */}
        <circle
          cx="87"
          cy="63"
          r="3"
          fill="white"
        />
        <circle
          cx="117"
          cy="63"
          r="3"
          fill="white"
        />
        
        {/* Nose */}
        <ellipse
          cx="100"
          cy="82"
          rx="6"
          ry="4"
          fill="#2C3E50"
        />
        
        {/* Mouth - Animated when talking */}
        <g
          style={{
            transformOrigin: '100px 95px',
            animation: isPlaying ? `${talkAnimation} 0.3s ease-in-out infinite` : 'none',
          }}
        >
          <path
            d="M 90 88 Q 100 95 110 88"
            stroke="#2C3E50"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Open mouth when talking */}
          {isPlaying && (
            <ellipse
              cx="100"
              cy="92"
              rx="8"
              ry="5"
              fill="#2C3E50"
              opacity="0.3"
            />
          )}
        </g>
        
        {/* Left Paw */}
        <ellipse
          cx="75"
          cy="160"
          rx="12"
          ry="20"
          fill="#4FC3C0"
        />
        
        {/* Right Paw */}
        <ellipse
          cx="125"
          cy="160"
          rx="12"
          ry="20"
          fill="#4FC3C0"
        />
        
        {/* Tail - wags when playing */}
        <path
          d="M 145 130 Q 170 120 175 100"
          stroke="#20B2AA"
          strokeWidth="12"
          fill="none"
          strokeLinecap="round"
          style={{
            transformOrigin: '145px 130px',
            animation: isPlaying ? `${wiggleAnimation} 0.4s ease-in-out infinite` : 'none',
          }}
        />
        
        {/* Sound Waves when playing */}
        {isPlaying && (
          <>
            <path
              d="M 150 60 Q 155 60 160 55"
              stroke="#20B2AA"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.6"
              style={{
                animation: `${pulseGlow} 0.8s ease-in-out infinite`,
              }}
            />
            <path
              d="M 155 70 Q 165 70 175 65"
              stroke="#20B2AA"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.4"
              style={{
                animation: `${pulseGlow} 0.8s ease-in-out infinite 0.2s`,
              }}
            />
            <path
              d="M 150 80 Q 160 80 170 75"
              stroke="#20B2AA"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.5"
              style={{
                animation: `${pulseGlow} 0.8s ease-in-out infinite 0.4s`,
              }}
            />
          </>
        )}
      </svg>
    </Box>
  );
};

export default AnimatedMascot;



